<template>
  <div>
    <div v-if="updatingProjects === 'success'" id="table-ready" />
    <a id="csv-export-link" ref="#" @click="fetchProjectCsv"
      ><b-img
        :src="raftHelper.getCsvIconPath()"
        width="24"
        height="24"
        fluid
        alt="CSV Download"
    /></a>

    <b-form>
      <fieldset class="biological-project-listing-filters">
        <legend>Project Filters</legend>
        <b-container>
          <b-row>
            <b-col class="raft-js-filter-cols">
              <short-text-filter
                id="project-title"
                label="Title"
                :get-value="projectTitle"
                :update-method="updateFilter"
                path="projectTitle"
                placeholder="Project Title"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <short-text-filter
                id="project-number"
                label="Project Number"
                :get-value="projectNumber"
                :update-method="updateFilter"
                path="projectNumber"
                placeholder="Project Number"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <short-text-filter
                id="approval-number"
                label="Approval Number"
                :get-value="approvalNumber"
                :update-method="updateFilter"
                path="approvalNumber"
                placeholder="Approval Number"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="bioSafetyLevels"
                id="bsl-sel"
                label="Biological Safety Level"
                path="selectedBsl"
                :current-set-value="selectedBsl"
                :multiple="true"
                track-by="value"
                display-value="label"
                :update-method="updateFilter"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="animalBioSafetyLevels"
                id="absl-sel"
                label="Animal Biological Safety Level"
                path="selectedAnimalBsl"
                :current-set-value="selectedAnimalBsl"
                :multiple="true"
                track-by="value"
                display-value="label"
                :update-method="updateFilter"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="largeScaleBioSafetyLevels"
                id="lsbsl-sel"
                label="Large Scale Biological Safety Level"
                path="selectedLargeBsl"
                :current-set-value="selectedLargeBsl"
                :multiple="true"
                track-by="value"
                display-value="label"
                :update-method="updateFilter"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="plantBioSafetyLevels"
                id="pbsl-sel"
                label="Plant Biosafety Level"
                path="selectedPlantBsl"
                :current-set-value="selectedPlantBsl"
                :multiple="true"
                track-by="value"
                display-value="label"
                :update-method="updateFilter"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="getAllLabs"
                id="group-fil"
                :multiple="true"
                label="Group"
                zort_store-watch="getSelectedGroups"
                display-value="title"
                track-by="nid"
                path="selectedGroups"
                :current-set-value="selectedGroups"
                :update-method="updateFilter"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <multi-select-filter
                :get-value="statusOptions"
                id="status-fil"
                :multiple="false"
                :is-required="true"
                label="Status"
                display-value="title"
                track-by="key"
                path="selectedStatus"
                :current-set-value="selectedStatus"
                :update-method="updateFilter"
              />
            </b-col>
            <b-col class="raft-js-filter-cols">
              <toggle-filter
                :get-value="includeInactiveGroups"
                id="include-inactive-groups"
                data-test="include-inactive-groups"
                label="Include Inactive Groups"
                :update-method="updateFilter"
                path="includeInactiveGroups"
              />
            </b-col>
          </b-row>
        </b-container>
      </fieldset>
    </b-form>

    <BaseTable
      :items="bioProjects"
      :fields="fields"
      :total-items="totalItems"
      :current-page="currentPage"
      :is-busy="updatingProjects === 'busy'"
      class-prefix="bio-projects"
      @update-current-page="updateCurrentPage"
      ref="table"
      :no-local-sorting="true"
      @sort-changed="sortingChanged"
    >
      <template #empty>
        <div class="empty-text">
          There are currently no biological projects to list.
        </div>
      </template>
      <template #title="row">
        <a :href="'/node/' + row.data.item.drupal_internal__nid">{{
          row.data.item.title
        }}</a>
      </template>
      <template #groups="row">
        <template v-for="(group, index) in row.data.item.og_audience">
          <template v-if="index > 0"> &nbsp;|&nbsp; </template>
          <a
            :key="group.drupal_internal__nid"
            :href="'/node/' + group.drupal_internal__nid"
            >{{ group.title }}</a
          >
        </template>
      </template>
    </BaseTable>

    <notifications position="bottom right" group="alerts" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import BaseTable from "../raft/BaseTable.vue";
import { jsonClient, restClient } from "../../lib/raftApi";
import JsonApiFilterManager from "../../lib/JsonApiFilterManager";
import ShortTextFilter from "../filters/ShortTextFilter.vue";
import MultiSelectFilter from "../filters/MultiSelectFilter.vue";
import raftGenericHelper from "../../lib/raft_generic_helper";
import ToggleFilter from "../filters/ToggleFilter.vue";

export default {
  name: "BioProjectListing",
  inject: ["isB4"],
  components: {
    ToggleFilter,
    MultiSelectFilter,
    BaseTable,
    ShortTextFilter,
  },
  props: {
    nodeId: { type: [String, Number], required: false, default: 0 },
  },
  data() {
    return {
      raftHelper: raftGenericHelper,
      items: [],
      bioProjects: [],
      updatingProjects: null,
      totalItems: 0,
      itemsPerPage: 25,
      currentPage: 1,
      sortDesc: false,
      sortBy: null,
      messageTypes: {},
      userSpecific: false,
      user: null,
      baseQueries: {
        include: "uid,og_audience",
        "fields[node--laboratory]": "title,drupal_internal__nid",
        "fields[node--department]": "title,drupal_internal__nid",
        "fields[node--raft_unit]": "title,drupal_internal__nid",
        sort: "-changed",
      },
      perms: [],
      projectTitle: "",
      projectNumber: "",
      approvalNumber: "",
      selectedBsl: [],
      selectedAnimalBsl: [],
      selectedLargeBsl: [],
      selectedPlantBsl: [],
      bioSafetyLevels: [],
      animalBioSafetyLevels: [],
      largeScaleBioSafetyLevels: [],
      plantBioSafetyLevels: [],
      selectedGroups: [],
      selectedStatus: { key: 1, title: "Active" },
      statusOptions: [
        { key: -1, title: "Any" },
        { key: 0, title: "Inactive" },
        { key: 1, title: "Active" },
      ],
      includeInactiveGroups: false,
    };
  },
  async created() {
    await this.fetchBioSafetyLevels();
    await this.fetchAllLabList();
    await this.debouncedFetchProjects(this);
  },
  methods: {
    ...mapActions(["fetchAllLabList"]),
    debouncedFetchProjects: debounce((ctx) => {
      ctx.fetchProjects();
    }, 100),
    async fetchBioSafetyLevels() {
      const path = "/biological_safety_levels";
      const queries = {};
      const restResponseData = await restClient
        .get({ path, queries })
        .catch((e) => {
          this.$emit("fetch-bls-levels-error", e);
        });
      this.bioSafetyLevels = restResponseData?.bioSafetyLevels;
      this.animalBioSafetyLevels = restResponseData?.animalBioSafetyLevels;
      this.largeScaleBioSafetyLevels =
        restResponseData?.largeScaleBioSafetyLevels;
      this.plantBioSafetyLevels = restResponseData?.plantBioSafetyLevels;
    },
    async fetchProjects() {
      this.updatingProjects = "busy";
      this.setDefaultFilters();
      this.setUpdatedFilters();
      if (this.currentPage > 1) {
        JsonApiFilterManager.setPageOffset(
          (this.currentPage - 1) * this.itemsPerPage,
        );
      } else {
        JsonApiFilterManager.setPageOffset(0);
      }
      if (this.sortBy) {
        JsonApiFilterManager.setSortFields(this.sortBy);
      }
      const options = {
        path: "node/bio_research_project",
        queries: JsonApiFilterManager.queries,
      };
      const response = await jsonClient
        .get(options)
        .catch((e) => this.$showNotification("error", e.message));
      if (response) {
        this.bioProjects = response.deserialized;
        this.totalItems = Number.parseInt(response.meta.count, 10);
      }
      this.updatingProjects = "success";
    },
    async fetchProjectCsv() {
      this.setDefaultFilters();
      this.setUpdatedFilters();
      let fileName = "BiologicalProjects.csv";
      const date = new Date().toLocaleDateString("en-CA", {});
      fileName = date + "_" + fileName;
      JsonApiFilterManager.setPageOffset(0);
      if (this.sortBy) {
        JsonApiFilterManager.setSortFields(this.sortBy);
      }
      const options = {
        path: "node/bio_research_project",
        queries: JsonApiFilterManager.queries,
      };
      const bioProjects = await jsonClient
        .getAllResults(options)
        .catch((e) => this.$showNotification("error", e.message));
      const headers = this.fields.map((a) => a.label);
      await jsonClient.formatResultsAsCsv(
        bioProjects,
        fileName,
        headers,
        (project) => [
          project.title ?? "",
          project.field_brp_proj_number ?? "",
          project.field_brp_approval_number ?? "",
          this.formatSafetyLevels("", "", project),
          this.formatGroupsCsv(project),
        ],
      );
    },
    setDefaultFilters() {
      JsonApiFilterManager.queries = this.baseQueries;
      JsonApiFilterManager.queries["page[limit]"] = this.itemsPerPage;
      JsonApiFilterManager.addFilter("status", "status", "1");
      JsonApiFilterManager.addFilter(
        "operative_status",
        "operative_status",
        "1",
      );
      JsonApiFilterManager.setSortFields("-changed");
    },
    setStatusFilters() {
      if (!Object.hasOwn(this.selectedStatus, "key")) {
        return;
      }
      const status = this.selectedStatus.key;
      if (status === -1) {
        JsonApiFilterManager.deleteFiltersByName(["operative_status"]);
        return;
      }
      JsonApiFilterManager.addFilter(
        "operative_status",
        "operative_status",
        status,
      );
    },
    setGroupStatusFilters() {
      if (this.includeInactiveGroups) {
        JsonApiFilterManager.deleteFiltersByName(["group_operative_status"]);
      } else {
        JsonApiFilterManager.addFilter(
          "group_operative_status",
          "og_audience.operative_status",
          "1",
        );
      }
    },
    setUpdatedFilters() {
      this.setStatusFilters();
      this.setGroupStatusFilters();

      const filters = {
        projectTitle: {
          path: "title",
          value: this.projectTitle,
          operator: "CONTAINS",
        },
        projectNumber: {
          path: "field_brp_proj_number",
          value: this.projectNumber,
          operator: "CONTAINS",
        },
        approvalNumber: {
          path: "field_brp_approval_number",
          value: this.approvalNumber,
          operator: "CONTAINS",
        },
        selBsl: {
          path: "field_brp_biosafety_level",
          value: this.selectedBsl,
        },
        selAnimalBsl: {
          path: "field_brp_absl",
          value: this.selectedAnimalBsl,
        },
        selPlantBsl: {
          path: "field_brp_plant_biosafety_level",
          value: this.selectedPlantBsl,
        },
        selLargeBsl: {
          path: "field_brp_bsl_ls",
          value: this.selectedLargeBsl,
        },
        selGroups: {
          path: "og_audience.meta.drupal_internal__target_id",
          value: this.selectedGroups,
          operator: "IN",
        },
      };
      Object.entries(filters).forEach(([name, filter]) => {
        if (filter.path === "operative_status") {
          if (typeof filter.value === "string") {
            filter.value = filter.value === "false" ? 0 : 1;
            JsonApiFilterManager.addFilter(
              name,
              filter.path,
              filter.value,
              filter.operator,
            );
          } else {
            JsonApiFilterManager.deleteFiltersByName([name]);
          }
        } else if (
          [
            "field_brp_biosafety_level",
            "field_brp_absl",
            "field_brp_plant_biosafety_level",
            "field_brp_bsl_ls",
          ].includes(filter.path)
        ) {
          if (filter?.value?.length > 0) {
            const selectedItems = filter.value.map((a) => a.value);
            JsonApiFilterManager.addMultivalueNamedFilter(
              filter.path + "-fil",
              filter.path,
              selectedItems,
            );
          } else {
            JsonApiFilterManager.deleteFiltersByName([filter.path + "-fil"]);
          }
        } else if (
          ["og_audience.meta.drupal_internal__target_id"].includes(filter.path)
        ) {
          if (filter?.value?.length > 0) {
            const selectedItems = filter.value.map((a) => a.nid);
            JsonApiFilterManager.addMultivalueNamedFilter(
              filter.path + "-fil",
              filter.path,
              selectedItems,
            );
          } else {
            JsonApiFilterManager.deleteFiltersByName([filter.path + "-fil"]);
          }
        } else if (filter.value) {
          JsonApiFilterManager.addFilter(
            name,
            filter.path,
            filter.value,
            filter.operator,
          );
        } else {
          JsonApiFilterManager.deleteFiltersByName([name]);
        }
      });
      this.$emit("updated-filters", {
        queries: JsonApiFilterManager.queries,
      });
    },
    updateCurrentPage(page) {
      this.currentPage = page;
    },
    updateFilter(value, path) {
      this.$set(this, path, value);
      this.debouncedFetchProjects(this);
    },
    sortingChanged(ctx) {
      this.sortDesc = !ctx.sortDesc;
      const sortDir = this.sortDesc ? "-" : "";
      const field = this.fields.find(
        (fieldData) => fieldData.key === ctx.sortBy && fieldData.sortable,
      );
      if (field) {
        this.sortBy = sortDir + field.sortPath;
      }
      this.debouncedFetchProjects(this);
    },
    formatSafetyLevels(value, key, item) {
      const safetyLevels = [];
      if (item.field_brp_biosafety_level) {
        safetyLevels.push(this.getBslSafetyLevel(item));
      }
      if (item.field_brp_absl) {
        safetyLevels.push(this.getAnimalBslSafetyLevel(item));
      }
      if (item.field_brp_plant_biosafety_level) {
        safetyLevels.push(this.getPlantBslSafetyLevel(item));
      }
      if (item.field_brp_bsl_ls) {
        safetyLevels.push(this.getLargeBslSafetyLevel(item));
      }
      return safetyLevels.join(" | ");
    },
    formatGroupsCsv(project) {
      const groups = [];
      project.og_audience.forEach((group) => {
        groups.push(group.title);
      });
      return groups.join(" | ");
    },
    getBslSafetyLevel(item) {
      let label = "";
      this.bioSafetyLevels.forEach((level) => {
        if (
          level.value.toString() === item.field_brp_biosafety_level.toString()
        ) {
          label = level.label;
        }
      });
      return label;
    },
    getAnimalBslSafetyLevel(item) {
      let label = "";
      this.animalBioSafetyLevels.forEach((level) => {
        if (level.value.toString() === item.field_brp_absl.toString()) {
          label = level.label;
        }
      });
      return label;
    },
    getPlantBslSafetyLevel(item) {
      let label = "";
      this.plantBioSafetyLevels.forEach((level) => {
        if (
          level.value.toString() ===
          item.field_brp_plant_biosafety_level.toString()
        ) {
          label = level.label;
        }
      });
      return label;
    },
    getLargeBslSafetyLevel(item) {
      let label = "";
      this.largeScaleBioSafetyLevels.forEach((level) => {
        if (level.value.toString() === item.field_brp_bsl_ls.toString()) {
          label = level.label;
        }
      });
      return label;
    },
  },
  computed: {
    ...mapGetters(["getAllLabs"]),
    fields: function () {
      return [
        {
          key: "title",
          label: "Project Title",
          sortable: false,
          custom_slot: "title",
        },
        {
          key: "field_brp_proj_number",
          label: "Project Number",
          sortable: false,
        },
        {
          key: "field_brp_approval_number",
          label: "Approval Number",
          sortable: false,
        },
        {
          key: "safety_levels",
          label: "Containment levels/Safety levels",
          formatter: this.formatSafetyLevels,
          sortable: false,
        },
        {
          key: "og_audience",
          label: "Group",
          sortable: false,
          custom_slot: "groups",
        },
      ];
    },
  },
  watch: {
    currentPage: function (newPage) {
      this.bioProjects = [];
      JsonApiFilterManager.queries["page[offset]"] =
        (newPage - 1) * this.itemsPerPage;
      this.debouncedFetchProjects(this);
    },
  },
};
</script>

<style lang="css" scoped src="../../css/B3BaseTable.css"></style>
<style lang="css" scoped src="../../css/B3Pagination.css"></style>
<style scoped>
:is(#raft-js, #raft-js-app)
  .biological-project-listing-filters
  .raft-js-filter-cols {
  width: 100%;
  float: none;
  margin: 0 0;
}
</style>
